import SVG from "react-inlinesvg";
import { cn } from "src/lib/cn";
import { Input } from "src/sudokit/components/Input";
import { SudoButton } from "src/sudokit/components/SudoButton";
import type { SudoButtonProps } from "src/sudokit/components/SudoButton";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip";
import { normalizeLinkHref } from "./linkUtils";

export type LinkEditorMode = "preview" | "edit";

interface CardIconButtonProps {
  icon: string;
  label: string;
  variant: SudoButtonProps["variant"];
  onClick: () => void;
  className?: string;
}

// Same geometry as the editor toolbar's icon buttons: 40px button, 24px icon
const CardIconButton = ({ icon, label, variant, onClick, className }: CardIconButtonProps) => (
  <SudoTooltip text={label} hasArrow={false} position="bottom">
    <SudoButton variant={variant} aria-label={label} className={cn("h-10 w-10 min-w-0 px-0", className)} onClick={onClick}>
      <SVG src={icon} className="w-6 h-6 [&_path]:fill-current" />
    </SudoButton>
  </SudoTooltip>
);

interface LinkEditorCardProps {
  mode: LinkEditorMode;
  /** Input value in edit mode; the link's href in preview mode */
  url: string;
  error?: boolean;
  /** Show the remove-link (trash) button — hidden when creating a brand-new link */
  showRemove?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  onUrlChange?: (value: string) => void;
  onInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onApply?: () => void;
  onStartEdit?: () => void;
  onRemove?: () => void;
  className?: string;
}

/**
 * The link editing card shared by the toolbar LinkPopover (edit mode) and the
 * in-document LinkBubble (preview mode, pencil flips it to edit mode).
 */
// The selection action menu's surface is contrast-inverted (--grey-0 +
// --context-menu-text flip against the theme), so everything neutral on the
// card must use the same inverted pair — theme-side tokens (grey-1, grey-3,
// CTA colors) turn invisible here in one mode or the other.
const INVERTED_HOVER = "hover:before:bg-[color-mix(in_srgb,var(--context-menu-text)_10%,transparent)] focus-visible:before:bg-[color-mix(in_srgb,var(--context-menu-text)_10%,transparent)] active:before:bg-[color-mix(in_srgb,var(--context-menu-text)_20%,transparent)]";

export const LinkEditorCard = ({ mode, url, error, showRemove, inputRef, onUrlChange, onInputKeyDown, onApply, onStartEdit, onRemove, className }: LinkEditorCardProps) => (
  <div className={cn("flex items-center gap-2 rounded-md bg-[var(--grey-0)] p-3 [box-shadow:var(--high-contrast-drop-shadow)]", className)}>
    {mode === "edit" ? (
      <>
        <Input
          ref={inputRef}
          mode="default"
          value={url}
          error={error}
          placeholder="https://example.com"
          aria-label="Link URL"
          containerClassName="w-64"
          className={cn(
            "text-[var(--context-menu-text)] placeholder:text-[var(--context-menu-text)] placeholder:opacity-50",
            !error && "border-[var(--context-menu-text)] focus:border-[var(--context-menu-text)]"
          )}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUrlChange?.(e.target.value)}
          onKeyDown={onInputKeyDown}
        />
        <SudoButton variant="primary" size="xsmall" className="h-10 min-w-10 px-2" onClick={onApply} aria-label="Apply link">
          <SVG src="/assets/check-simple.svg" className="w-5 h-5 [&_path]:fill-current" />
        </SudoButton>
      </>
    ) : (
      <>
        <a
          // Stored hrefs can predate sanitization; never make an unsafe
          // scheme (javascript:, data:, …) clickable — display it only.
          href={normalizeLinkHref(url) ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 max-w-64 px-1 text-sm text-[var(--context-menu-text)] underline"
          aria-label="Open link in new tab"
        >
          <span className="truncate">{url}</span>
          <SVG src="/assets/open-in-new.svg" className="w-6 h-6 shrink-0 [&_path]:fill-current" />
        </a>
        <CardIconButton icon="/assets/pencil.svg" label="Edit link" variant="transparent" className={cn("text-[var(--context-menu-text)]", INVERTED_HOVER)} onClick={() => onStartEdit?.()} />
      </>
    )}

    {showRemove && <CardIconButton icon="/assets/delete.svg" label="Remove link" variant="error" onClick={() => onRemove?.()} />}
  </div>
);
